import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import {
  ShoppingCart,
  AttachMoney,
  TrendingUp,
  Schedule,
} from '@mui/icons-material';
import firebase from 'firebase/app';
import { useRegionContext } from '../../region/RegionContext';
import { queryAllRegions } from '../../utils/queryAllRegions';
import TabDescription from './TabDescription';
import 'firebase/firestore';

const PAGE_SIZE = 25;

const OrdersTab = () => {
  const { db, isGlobal, allRegionDbs } = useRegionContext();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFilter, setDateFilter] = useState('30days');
  const [page, setPage] = useState(0);

  useEffect(() => {
    loadOrders();
  }, [db, isGlobal, allRegionDbs, dateFilter]);

  const loadOrders = async () => {
    try {
      setLoading(true);

      let data;

      const buildQuery = (regionDb) => {
        let q = regionDb.collection('Orders');
        if (dateFilter !== 'all') {
          const cutoff = new Date();
          if (dateFilter === '7days') cutoff.setDate(cutoff.getDate() - 7);
          else if (dateFilter === '30days') cutoff.setDate(cutoff.getDate() - 30);
          else if (dateFilter === '90days') cutoff.setDate(cutoff.getDate() - 90);
          q = q.where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(cutoff));
        }
        return q.limit(100);
      };

      if (isGlobal && allRegionDbs) {
        data = await queryAllRegions(allRegionDbs, null, buildQuery);
      } else {
        const snapshot = await buildQuery(db).get();
        data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
      }

      // Sort client-side by timestamp descending
      data.sort((a, b) => {
        const aTime = a.timestamp?.toDate?.()?.getTime?.() || 0;
        const bTime = b.timestamp?.toDate?.()?.getTime?.() || 0;
        return bTime - aTime;
      });

      setOrders(data);
      setPage(0);
    } catch (err) {
      console.error('Error loading orders:', err);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const totalRevenue = orders.reduce((sum, o) => sum + (o.orderTotal || 0), 0);
  const statusCounts = {};
  orders.forEach(o => {
    const status = o.orderStatus || 'unknown';
    statusCounts[status] = (statusCounts[status] || 0) + 1;
  });

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading orders...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <TabDescription>
        Order activity from the Orders Firestore collection, filtered by the selected date range. Summary
        cards show total orders, revenue, average order value, and status breakdown (pending, completed,
        cancelled). Use this to track revenue trends, identify order fulfilment issues, and monitor
        platform transaction health.
      </TabDescription>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ShoppingCart sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">Total Orders</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>{orders.length}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AttachMoney sx={{ fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">Total Revenue</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>${totalRevenue.toFixed(2)}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUp sx={{ fontSize: 40, color: 'info.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">Avg Order Value</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    ${orders.length > 0 ? (totalRevenue / orders.length).toFixed(2) : '0.00'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {Object.entries(statusCounts).map(([status, count]) => (
                  <Chip
                    key={status}
                    label={`${status}: ${count}`}
                    size="small"
                    color={status === 'completed' ? 'success' : status === 'pending' ? 'warning' : 'default'}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Date Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              label="Date Range"
            >
              <MenuItem value="7days">Last 7 Days</MenuItem>
              <MenuItem value="30days">Last 30 Days</MenuItem>
              <MenuItem value="90days">Last 90 Days</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Orders ({orders.length})
          </Typography>

          {orders.length === 0 ? (
            <Alert severity="info">No orders found for the selected period</Alert>
          ) : (
            <>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
              <Button variant="outlined" size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                Previous
              </Button>
              <Typography variant="body2">
                Page {page + 1} of {Math.ceil(orders.length / PAGE_SIZE)} ({orders.length} total)
              </Typography>
              <Button variant="outlined" size="small" disabled={(page + 1) * PAGE_SIZE >= orders.length} onClick={() => setPage(p => p + 1)}>
                Next
              </Button>
            </Box>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table aria-label="Orders list">
                <TableHead>
                  <TableRow>
                    <TableCell scope="col"><strong>Order ID</strong></TableCell>
                    <TableCell scope="col"><strong>Timestamp</strong></TableCell>
                    <TableCell scope="col"><strong>Venue</strong></TableCell>
                    <TableCell scope="col"><strong>User</strong></TableCell>
                    <TableCell scope="col"><strong>Status</strong></TableCell>
                    <TableCell scope="col" align="right"><strong>Total</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {order.id.substring(0, 12)}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Schedule sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption">{formatDate(order.timestamp)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{order.venueName || order.bizName || order.businessId?.substring(0, 12) || '—'}</TableCell>
                      <TableCell>{order.userName || order.userId?.substring(0, 12) || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={order.orderStatus || 'unknown'}
                          size="small"
                          color={
                            order.orderStatus === 'completed' ? 'success'
                              : order.orderStatus === 'pending' ? 'warning'
                              : order.orderStatus === 'cancelled' ? 'error'
                              : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {order.orderTotal != null ? `$${Number(order.orderTotal).toFixed(2)}` : '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrdersTab;
